import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { Game } from '../services/backendService/types';
import { Template } from '../services/TemplateService/Template.type';
import { useDesignerUIContext } from './useDesignerUIState';
import { useRawGame } from './useGame';
import useGameAutoSave from './useGameAutoSaver';
import useGenerateNewGamePin from './useGenerateNewGamePin';
import { useMetrics } from './useMetrics';
import usePresent from './usePresent';
import usePreviewGame from './usePreviewGame';
import useRouterMatch from './useRouterMatch';
import useTemplate from './useTemplate';

export const useDesignerSaveLogic = (localTemplate?: Template) => {
  const metrics = useMetrics();
  const {
    router,
    tenantId,
    urlTenantId,
    workspaceId,
    parentFolderId,
    templateId,
  } = useRouterMatch();
  const { presentLesson, isCreatingGame } = usePresent();
  const { data: game } = useRawGame(localTemplate?.content.activeGame?.id);
  useGenerateNewGamePin(game?.id, game?.pin);

  const { createPreviewGame, isLoadingPreview } = usePreviewGame();
  const { autoSave: autoSaveGame } = useGameAutoSave();
  const {
    template,
    isFetchingTemplate,
    autoSaveTemplate,
    isSavingTemplate,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
    isSavingTemplateError,
    error,
  } = useTemplate(templateId, null, tenantId);

  const forceLoadingState = useDesignerUIContext(c => c.state.forceLoadingState);
  const isUserIdle = useDesignerUIContext(c => c.state.isUserIdle);

  useEffect(() => {
    // if the user is not the owner of the template redirect to the preview page
    if (error?.response?.status === 403) {
      router.replace(`/${urlTenantId}/lessons/${templateId}`);
    }
  }, [error, router, templateId, urlTenantId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSave = useCallback(
    debounce(async (draft: Template, server?: Template) => {
      if (JSON.stringify(draft) !== JSON.stringify(server)) {
        autoSaveTemplate({
          templateConfig: {
            slides: draft.content.slides ? draft.content.slides : [],
            id: draft.id,
            title: !!draft.title ? draft.title : null,
            parentId: draft.parentId || parentFolderId,
            isPrivate: !!workspaceId ? false : draft.isPrivate === false ? false : true,
            description: !!draft.description
              ? draft.description
              : server?.description || null,
            coverImageAltText: !!draft.coverImageAltText
              ? draft.coverImageAltText
              : server?.coverImageAltText || null,
            requireRealNames:
              draft.requireRealNames !== undefined
                ? draft.requireRealNames
                : server?.requireRealNames || true,
            templateSharingPublic:
              draft.templateSharingPublic !== undefined
                ? draft.templateSharingPublic
                : server?.templateSharingPublic || false,
            isBlockedFromSharing:
              draft.isBlockedFromSharing !== undefined
                ? draft.isBlockedFromSharing
                : server?.isBlockedFromSharing || false,
            coverImageId: !!draft.coverImageId
              ? draft.coverImageId
              : server?.coverImageId || undefined,
            currentRound: draft.currentRound,
            modifiedAt: draft.modifiedAt,
            language: !draft.language ? server?.language : draft.language,
            aiContent: draft.aiContent,
            attachments: draft.attachments,
          },
        });
      }
    }, 1000),
    [],
  );

  useEffect(() => {
    if (!localTemplate) return;
    if (localTemplate && template && localTemplate.modifiedAt < template.modifiedAt)
      return; // make sure we dont override new changes with previous ones in case of race condition

    if (isUserIdle) return; // dont save if user is idle in the background. can cause issue with multiple tabs open with same designer
    if (forceLoadingState === 'loading') return; // dont save if we are in force loading state which means a force refresh is happening
    debouncedAutoSave(localTemplate, template);
    if (!template) return;

    if (template.content.activeGame?.isOngoing) return; // prevent overwriting active
    autoSaveGame(
      {
        slides: localTemplate.content.slides ? localTemplate.content.slides : [],
        gameId: template.content.activeGame?.id,
        currentRound: localTemplate.currentRound,
        title: localTemplate.title,
        isPlaying: false,
        roundStartsAt: null,
        lastPlayedAt: null,
        requireRealNames: localTemplate.requireRealNames,
        modifiedAt: localTemplate.modifiedAt,
        attachments: localTemplate.attachments,
      },
      undefined,
      template,
    );
  }, [
    localTemplate,
    template,
    autoSaveGame,
    debouncedAutoSave,
    forceLoadingState,
    isUserIdle,
  ]);

  const handlePresent = useCallback(() => {
    if (localTemplate) {
      metrics.logEvent('Creator.Present', { templateId: localTemplate?.id });
      presentLesson({
        template: localTemplate,
        method: 'present',
        gameId: template?.content.activeGame?.id,
        fromBeginning: template?.content.activeGame?.isOngoing ? false : true,
      });
    }
  }, [
    localTemplate,
    metrics,
    presentLesson,
    template?.content.activeGame?.id,
    template?.content.activeGame?.isOngoing,
  ]);

  const handlePresentWithModeration = useCallback(() => {
    if (localTemplate) {
      metrics.logEvent('Creator.Present', {
        templateId: localTemplate?.id,
        withModeration: true,
      });
      presentLesson({
        template: localTemplate,
        method: 'curimote',
        gameId: template?.content.activeGame?.id,
      });
    }
  }, [localTemplate, metrics, presentLesson, template?.content.activeGame?.id]);

  const handlePresentFromBeginning = useCallback(
    (localTemplate: Template, callbackBeforeRedirect?: (gameId: string) => void) => {
      metrics.logEvent('Creator.Present', {
        templateId: localTemplate?.id,
        fromBeginning: true,
      });

      presentLesson({
        template: { ...localTemplate },
        method: 'present',
        fromBeginning: true,
        gameId: localTemplate.content.activeGame?.id,
        callbackBeforeRedirect,
      });
    },
    [metrics, presentLesson],
  );

  const handleOpenPreview = useCallback(() => {
    if (tenantId) {
      createPreviewGame({
        templateId: templateId,
        template: localTemplate,
        onSuccessFunction: (data: Game) => {
          metrics.logEvent('Preview.Open', {
            templateId: localTemplate?.id,
          });
          router.push(`/${tenantId}/lessons/${localTemplate?.id}/preview/${data.id}`);
        },
      });
    }
  }, [createPreviewGame, localTemplate, metrics, router, templateId, tenantId]);

  const isPresentingDisabled = useMemo(
    () =>
      (localTemplate?.content?.slides || []).length < 1 ||
      !game ||
      !game.slides ||
      game.slides.length < 1,
    [game, localTemplate?.content?.slides],
  );

  return {
    handlePresent,
    handlePresentWithModeration,
    isSavingTemplate,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
    isSavingTemplateError,
    isFetchingTemplate,
    isCreatingGame,
    handlePresentFromBeginning,
    createPreviewGame,
    isLoadingPreview,
    handleOpenPreview,
    autoSaveTemplate,
    isPresentingDisabled,
    game,
  };
};
